import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogIn, UserPlus } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../lib/api';

const AdminLoginPage = () => {
  const { login, signup, user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mode, setMode] = useState('login');
  const [signupAvailable, setSignupAvailable] = useState(false);
  const [form, setForm] = useState({ username: '', email: '', password: '' });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate('/admin', { replace: true });
    api.get('/auth/signup-available').then((r) => {
      setSignupAvailable(r.data.available);
      if (r.data.available) setMode('signup');
    }).catch(() => {});
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === 'signup') {
        await signup(form.username, form.email, form.password);
        toast({ title: 'Account created', description: 'Welcome!' });
      } else {
        await login(form.username, form.password);
        toast({ title: 'Signed in' });
      }
      navigate('/admin', { replace: true });
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Something went wrong' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#05080f] grid-bg flex items-center justify-center px-6">
      <div className="w-full max-w-md card-dark rounded-3xl p-8">
        <Link to="/" className="text-sm text-slate-400 hover:text-amber-300">&larr; Back to site</Link>
        <h1 className="font-display text-3xl text-white font-semibold mt-4 mb-1">
          {mode === 'signup' ? 'Create admin account' : 'Admin Login'}
        </h1>
        <p className="text-slate-400 text-sm mb-6">
          {mode === 'signup'
            ? 'This will be the primary admin. Only one signup is allowed.'
            : 'Sign in to manage AI products.'}
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs text-slate-400">Username</label>
            <Input
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="mt-2 bg-white/[0.03] border-white/10 text-white placeholder:text-slate-600 h-11"
              required
            />
          </div>
          {mode === 'signup' && (
            <div>
              <label className="text-xs text-slate-400">Email</label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-2 bg-white/[0.03] border-white/10 text-white placeholder:text-slate-600 h-11"
                required
              />
            </div>
          )}
          <div>
            <label className="text-xs text-slate-400">Password</label>
            <Input
              type="password"
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="mt-2 bg-white/[0.03] border-white/10 text-white placeholder:text-slate-600 h-11"
              required
              minLength={6}
            />
          </div>

          <Button type="submit" disabled={busy} className="w-full btn-gradient rounded-full h-11 text-white border-0">
            {busy ? 'Please wait...' : (
              mode === 'signup'
                ? (<span className="inline-flex items-center gap-2"><UserPlus size={16} /> Create account</span>)
                : (<span className="inline-flex items-center gap-2"><LogIn size={16} /> Sign in</span>)
            )}
          </Button>

          {!signupAvailable && (
            <p className="text-xs text-slate-500 text-center">
              Signup is disabled. Contact the primary administrator.
            </p>
          )}
        </form>
      </div>
    </div>
  );
};

export default AdminLoginPage;
